import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Share2 } from "lucide-react";
import { api, fmtL } from "@/lib/api";
import { TeamBadge, PlayerAvatar, Spinner, EmptyState } from "@/components/opl/Common";

const Box = ({ v, l, gold }) => (
  <div className="bg-[#F7F6F2] rounded-xl p-3"><div className={`num font-bold text-sm sm:text-lg truncate ${gold ? "text-amber-700" : "text-[#0F172A]"}`}>{v}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div></div>
);

function TeamCard({ t, d, open, setOpen }) {
  const isOpen = open === t.id;
  return (
    <div className="card-l p-5 sm:p-6 min-w-0 hover:!transform-none" data-testid={`public-team-${t.code}`} style={{ borderTop: `4px solid ${t.color || "#D97706"}` }}>
      <div className="flex items-start gap-4">
        <TeamBadge team={t} size={64} />
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold truncate text-[#0F172A]">{t.name}</h2>
          <p className="text-sm text-slate-500 truncate">{t.club_name} · Owner {t.owner_name}</p>
          <p className="text-xs text-slate-400 mt-1">Captain: {t.captain || "TBA"} · Manager: {t.manager || "TBA"}</p>
        </div>
      </div>
      <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-2 mt-5 text-center">
        <Box v={t.players_count} l="Players" />
        <Box v={d.show_purse ? fmtL(t.spent) : "—"} l="Spent" />
        <Box v={d.show_purse ? fmtL(t.remaining_purse) : "—"} l="Purse left" gold />
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {Object.entries(t.roles).map(([r, n]) => <span key={r} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1">{r}: <b>{n}</b></span>)}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button className="text-sm font-semibold text-[#0F172A] flex items-center gap-1" onClick={() => setOpen(isOpen ? null : t.id)} data-testid={`toggle-squad-${t.code}`}>
          {isOpen ? "Hide squad" : `View squad (${t.squad.length})`}<ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {t.squad.length > 0 && <Link to={`/poster/${t.id}`} className="navy-btn px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5" data-testid={`squad-poster-link-${t.code}`}><Share2 className="h-3.5 w-3.5 text-amber-400" />Squad card</Link>}
      </div>
      {isOpen && (
        <div className="mt-3 space-y-2">
          {t.squad.map((p) => (
            <div key={p.id} className="flex items-center gap-3 bg-[#F7F6F2] rounded-xl p-2">
              <PlayerAvatar player={p} size={32} />
              <span className="flex-1 text-sm text-[#0F172A]">{p.name} <span className="text-slate-500">· {p.role}</span></span>
              {p.price != null && <span className="num text-sm font-semibold text-amber-700">{fmtL(p.price)}</span>}
            </div>
          ))}
          {!t.squad.length && <p className="text-sm text-slate-500">No players acquired yet</p>}
        </div>
      )}
    </div>
  );
}

export default function PublicTeams() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [club, setClub] = useState("");
  useEffect(() => { api.get("/public/teams").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  const clubs = [...new Set(d.teams.map((t) => t.club_name).filter(Boolean))];
  const list = d.teams.filter((t) => !club || t.club_name === club);
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
      <p className="eyebrow">OPL Season 6</p>
      <h1 className="text-4xl sm:text-5xl font-bold mt-2 text-[#0F172A]">Teams</h1>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-8">
        <div className="flex gap-2 bg-[#F4F2EC] rounded-full p-1 w-fit" data-testid="teams-club-filter">
          {["", ...clubs].map((c) => (
            <button key={c || "all"} onClick={() => setClub(c)} data-testid={`club-select-${(c || "all").toLowerCase()}`}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${club === c ? "bg-white shadow-sm text-[#0F172A]" : "text-slate-500"}`}>{c || "All clubs"}</button>
          ))}
        </div>
        <div className="flex gap-2">
          {clubs.map((c) => (
            <Link key={c} to={`/poster/club/${c.toLowerCase()}`} data-testid={`club-poster-link-${c.toLowerCase()}`}
              className="navy-btn px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5 text-amber-400" />{c} poster</Link>
          ))}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="public-teams-list">
        {list.map((t) => <TeamCard key={t.id} t={t} d={d} open={open} setOpen={setOpen} />)}
      </div>
      {!d.teams.length && <EmptyState title="No teams yet" />}
    </div>
  );
}
